import { useMemo } from 'react';

import styled from 'styled-components';

import { TickOutlined } from '@aircall/icons';
import { Button, ButtonProps, ThemeProvider } from '@aircall/tractor-v2';
import { Loading } from '@dashboard/library';
import { useTranslation } from 'react-i18next';

export const AnimatedButton = styled(Button)`
  transition: 0.5s all ease-in-out;
`;

export const SaveButtonStatus = {
  UNSAVED: 'unsaved',
  SAVING: 'saving',
  SAVED: 'saved',
} as const;

export type Status = typeof SaveButtonStatus[keyof typeof SaveButtonStatus];
export type SaveButtonMode = 'link' | 'fill' | 'outline';

export interface SaveButtonProps extends Omit<ButtonProps, 'children' | 'mode'> {
  /** The status of save button */
  status: Status;
  /** The text to display initially */
  content?: string;
  /** The text to display after having saved user's input */
  savedContent?: string;
  /** The mode of Save button */
  mode?: SaveButtonMode;
  /** The testing key */
  'data-test'?: string;
}

export function SaveButton({
  mode = 'fill',
  status,
  content,
  savedContent,
  ...rest
}: SaveButtonProps) {
  const { t } = useTranslation();
  const buttonContent = useMemo(
    () => ({
      unsaved: content || t(`app.form.buttons.save.unsaved`),
      saving: (
        <ThemeProvider>
          <Loading
            size={24}
            data-test='saving-icon'
            color={mode === 'fill' ? 'white' : 'interaction-primary'}
          />
        </ThemeProvider>
      ),
      saved: savedContent || t(`app.form.buttons.save.saved`),
    }),
    [content, savedContent, t, mode]
  );

  const buttonMode = useMemo(() => {
    if (mode === 'fill') {
      return status === SaveButtonStatus.SAVED ? 'outline' : 'fill';
    }
    return mode;
  }, [mode, status]);

  return (
    <ThemeProvider>
      <AnimatedButton
        data-test='save-button'
        mode={buttonMode}
        readOnly={status !== SaveButtonStatus.UNSAVED}
        {...rest}
      >
        {status === SaveButtonStatus.SAVED && <TickOutlined />}
        {buttonContent[status]}
      </AnimatedButton>
    </ThemeProvider>
  );
}
